import React, { useCallback, useRef, useState } from 'react'
import { Alert, Button, message, Modal } from '@surecompanies/core_components'
import InboxOutlined from '@ant-design/icons/InboxOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import DownloadOutlined from '@ant-design/icons/DownloadOutlined'
import {
  importNewPlanBenefits,
  type ImportNewBenefitsResponseDto,
} from '../../../api/plan-management-tasks-api'
import { downloadPlanBenefitTemplate } from '../../PlanOverrides/pages/PlanBenefitOverrides/utils/downloadPlanBenefitTemplate'

interface Props {
  taskId: string
  open: boolean
  onClose: () => void
  onDone: () => void
}

const MAX_FILE_SIZE = 5 * 1024 * 1024

type Phase = 'idle' | 'uploading' | 'result'

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function ImportNewBenefitsModal({
  taskId,
  open,
  onClose,
  onDone,
}: Props) {
  const [phase, setPhase] = useState<Phase>('idle')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [result, setResult] = useState<ImportNewBenefitsResponseDto | null>(
    null
  )
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const resetState = useCallback(() => {
    setPhase('idle')
    setSelectedFile(null)
    setErrorMessage(null)
    setResult(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const handleClose = useCallback(() => {
    if (phase === 'result') {
      onDone()
    }
    resetState()
    onClose()
  }, [phase, resetState, onClose, onDone])

  const validateAndSetFile = useCallback((file: File) => {
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      message.error('Only CSV files are allowed.')
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      message.error('File exceeds the 5 MB limit.')
      return
    }
    setSelectedFile(file)
    setErrorMessage(null)
  }, [])

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      validateAndSetFile(file)
      e.target.value = ''
    },
    [validateAndSetFile]
  )

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const handleImport = useCallback(async () => {
    if (!selectedFile) return
    setPhase('uploading')
    setErrorMessage(null)
    try {
      const res = await importNewPlanBenefits(taskId, selectedFile)
      setResult(res)
      setPhase('result')
      if (res.created + res.updated === 0 && res.errors.length === 0) {
        message.info('No new benefits were added — all rows exist in staging.')
      }
    } catch (err: unknown) {
      setPhase('idle')
      const axiosErr = err as {
        response?: { status?: number; data?: { message?: string } }
      }
      const status = axiosErr.response?.status
      if (status === 401 || status === 403) {
        message.error('You do not have permission to perform this action.')
      } else if (status === 422) {
        const serverMsg =
          axiosErr.response?.data?.message ?? 'CSV could not be processed.'
        setErrorMessage(serverMsg)
      } else {
        // eslint-disable-next-line no-console
        console.error('import new benefits failed', err)
        message.error('Import failed. Please try again.')
      }
    }
  }, [selectedFile, taskId])

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      if (phase === 'idle') setIsDragOver(true)
    },
    [phase]
  )

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDragOver(false)
      if (phase !== 'idle') return
      const file = e.dataTransfer.files?.[0]
      if (file) validateAndSetFile(file)
    },
    [phase, validateAndSetFile]
  )

  const isUploading = phase === 'uploading'
  const isResult = phase === 'result'
  const maxErrorsShown = 10

  return (
    <Modal
      title="Import New Benefits"
      open={open}
      onCancel={handleClose}
      footer={null}
      width={560}
      destroyOnClose
      maskClosable={false}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {!isResult && (
          <>
            <p
              style={{
                margin: 0,
                color: '#6b7280',
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              Upload a CSV file with plan benefits. Rows matching existing
              staging data are skipped; new rows are imported into the system.
            </p>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '10px 14px',
                background: '#f0f5ff',
                border: '1px solid #d6e4ff',
                borderRadius: 8,
              }}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  color: '#1f2937',
                }}
              >
                <FileTextOutlined style={{ color: '#1677ff', fontSize: 16 }} />
                Not sure about the format? Start from the template.
              </span>
              <Button
                type="primary"
                ghost
                size="small"
                icon={<DownloadOutlined />}
                onClick={downloadPlanBenefitTemplate}
                style={{ flexShrink: 0 }}
              >
                Download template
              </Button>
            </div>
          </>
        )}

        {errorMessage && <Alert type="error" showIcon message={errorMessage} />}

        {isResult && result && (
          <>
            <Alert
              type={result.created + result.updated > 0 ? 'success' : 'info'}
              showIcon
              message={`Imported ${result.created} new, ${result.updated} updated, ${result.skipped} skipped out of ${result.totalRows} rows.`}
            />
            {result.errors.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`${result.errors.length} row(s) had errors`}
                description={
                  <ul
                    style={{ margin: '4px 0 0', paddingLeft: 20, fontSize: 12 }}
                  >
                    {result.errors.slice(0, maxErrorsShown).map((e) => (
                      <li key={e.row}>
                        Row #{e.row}: {e.message}
                      </li>
                    ))}
                    {result.errors.length > maxErrorsShown && (
                      <li>
                        ...and {result.errors.length - maxErrorsShown} more
                      </li>
                    )}
                  </ul>
                }
              />
            )}
          </>
        )}

        {!isResult && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              disabled={isUploading}
              style={{ display: 'none' }}
              aria-label="Select CSV file"
            />

            {selectedFile ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                }}
              >
                <FileTextOutlined style={{ fontSize: 24, color: '#6b7280' }} />
                <span style={{ flex: 1 }}>
                  <span style={{ display: 'block', fontWeight: 500 }}>
                    {selectedFile.name}
                  </span>
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>
                    {formatFileSize(selectedFile.size)}
                  </span>
                </span>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleRemoveFile}
                  disabled={isUploading}
                  aria-label="Remove file"
                />
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  padding: '32px 16px',
                  border: `2px dashed ${isDragOver ? '#1677ff' : '#d9d9d9'}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                  backgroundColor: isDragOver ? '#f0f5ff' : undefined,
                }}
                onClick={() => !isUploading && fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                role="button"
                tabIndex={0}
                aria-label="Upload CSV file"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ')
                    fileInputRef.current?.click()
                }}
              >
                <InboxOutlined style={{ fontSize: 40, color: '#1677ff' }} />
                <span style={{ color: '#6b7280' }}>
                  Click or drag a CSV file here
                </span>
                <span style={{ fontSize: 12, color: '#bfbfbf' }}>
                  Max 5 MB &middot; .csv only
                </span>
              </div>
            )}
          </>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 8,
          }}
        >
          {isResult ? (
            <Button type="primary" onClick={handleClose}>
              Done
            </Button>
          ) : (
            <>
              <Button onClick={handleClose} disabled={isUploading}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleImport}
                loading={isUploading}
                disabled={!selectedFile || isUploading}
              >
                Import
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}
